import React, { useEffect, useRef, useState, useMemo } from 'react';
import { AlertBanner, DropdownMenu, Button, SparkIcon, TagFeedback, sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { getLanguageNameByCode } from '@/shared/constants/languageConstants';
import { useWalkthroughContextValue } from '@/features/walkthrough';
import { useTaskChangeStatus } from '@/features/tasks/hooks/useTaskChangeStatus';
import { resolveRevaluatePage } from '@/features/evaluation/utils/revaluateFlow';
import EvaluationRunStatus from './EvaluationRunStatus';
import RecalculateEvaluationParamsModal from './RecalculateEvaluationParamsModal';
import './FilterBar.css';

const FilterBar = ({
    compareOptions,
    versionOptions,
    selectedVersion,
    setSelectedVersion,
    currentToCompare,
    setCurrentToCompare,
    selectedLabel,
    setSelectedLabel,
    languageOptions = null,
    selectedLanguage = null,
    setSelectedLanguage = null,
    hideLanguageSelect = false,
    selectedMatchType = null,
    setSelectedMatchType = null,
    getClassOptions,
    dropdownLabelRender,
    onRefreshStart,
    onRefreshComplete,
    localUsecase,
    versionOptionsRaw,
    viewMode,
    setViewMode,
    moveToScreen,
    onImproveClick,
    showImproveEvals = true,
    improveButtonLabel = "Improve",
    hideLabelSelect = false,
    hideViewToggle = false,
    taskType,
    activeTab = 'entities',
    onPlayWithThreshold,
    isThresholdMode = false,
    onBackFromThreshold,
    runDateIso = null,
    isLatestEvaluation = null,
    runStatusVersion = null,
    onSwitchToLatest = null,
    evaluationProgress = null,
    onRetrain = null,
    // Map of major version (number) -> list of its evaluation-run minor versions
    // (e.g. { 5: [5.0, 5.01, 5.02] }); majors with more than one run get the
    // "replace benchmark" affordance in the "To" dropdown.
    minorVersionsByMajor = null,
    // Map of major version (number) -> the minor version whose benchmark is
    // currently active for that major (session lock or latest run).
    activeMinorByMajor = null,
    // Opens the "Replace Benchmark" modal for the given major version.
    onReplaceBenchmark = null,
}) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [paramsModalOpen, setParamsModalOpen] = useState(false);
    const [diffSide, setDiffSide] = useState(null);
    const [successDismissed, setSuccessDismissed] = useState(false);
    const [compareMenuOpen, setCompareMenuOpen] = useState(false);
    const [versionMenuOpen, setVersionMenuOpen] = useState(false);
    const sawRunningRef = useRef(false);
    const lastRunRef = useRef({ evaluationScope: 'full', benchmarkNames: null });
    const changeStatus = useTaskChangeStatus({ usecase: localUsecase, enabled: Boolean(localUsecase?._id) });
    const isSummarization = isGenerativeTask(taskType);
    const isNerTaskType = taskType === TaskType.NER;
    const latestVersion = versionOptionsRaw && versionOptionsRaw.length > 0 ? Math.max(...versionOptionsRaw) : null;
    const selectedMajorVersion = selectedVersion ? Math.floor(selectedVersion) : null;
    const latestMajorVersion = latestVersion ? Math.floor(latestVersion) : null;
    const isLatestMajor = selectedMajorVersion === latestMajorVersion;
    const progressStatus = evaluationProgress?.status || null;
    if (progressStatus && !['completed', 'failed', 'canceled'].includes(progressStatus)) {
        sawRunningRef.current = true;
    }
    useEffect(() => {
        if (progressStatus && !['completed', 'failed', 'canceled'].includes(progressStatus)) {
            setSuccessDismissed(false);
        }
    }, [progressStatus]);
    const errorDetails = evaluationProgress?.error_details;
    const failureReason = typeof errorDetails === 'string'
        ? errorDetails
        : (errorDetails?.reason || errorDetails?.message || '');
    const processed = Number(evaluationProgress?.processed);
    const progressTotal = Number(evaluationProgress?.total);
    const previousTeacher = (changeStatus.changes || []).find((row) => row.field === 'teacher_model')?.previous || '';
    let runStatus = null;
    if (progressStatus === 'failed' || progressStatus === 'canceled') runStatus = 'failed';
    else if (progressStatus === 'completed' && sawRunningRef.current && !successDismissed) runStatus = 'success';
    else if (progressStatus && progressStatus !== 'completed') runStatus = 'running';
    const page = resolveRevaluatePage({
        teacherModelChanged: changeStatus.teacherModelChanged,
        teacherPromptChanged: changeStatus.teacherPromptChanged,
        specificPromptChanged: changeStatus.specificPromptChanged,
        benchmarksChanged: changeStatus.benchmarksChanged,
        datasetsChanged: changeStatus.datasetsChanged,
        comparisonOn: Boolean(localUsecase?.comparison_mode),
        isGenerative: isSummarization,
        previousTeacher,
        currentTeacher: localUsecase?.teacher_model || '',
        benchmarkName: localUsecase?.benchmarks || [],
        versionLabel: selectedMajorVersion != null ? `V${selectedMajorVersion}.0` : '',
        runStatus,
        failureReason,
        progressText: Number.isFinite(processed) && Number.isFinite(progressTotal)
            ? `${processed} of ${progressTotal} records`
            : '',
        lastScope: lastRunRef.current.evaluationScope,
    });

    const majorVersionOptions = useMemo(() => (versionOptionsRaw || [])
        .map(v => Math.floor(Number(v)))
        .filter(v => Number.isFinite(v))
        .filter((v, idx, arr) => arr.indexOf(v) === idx)
        .sort((a, b) => b - a)
        .map(v => ({ label: `V${v}`, value: v })), [versionOptionsRaw]);

    // Returns "v5.02"-style label of the active benchmark run for a major
    // version, but only when that major has more than one evaluation run.
    const getActiveMinorLabel = useMemo(() => (major) => {
        const minors = minorVersionsByMajor?.[major] ?? minorVersionsByMajor?.[String(major)];
        if (!Array.isArray(minors) || minors.length <= 1) return null;
        const active = activeMinorByMajor?.[major]
            ?? activeMinorByMajor?.[String(major)]
            ?? Math.max(...minors.map(Number));
        const num = Number(active);
        if (!Number.isFinite(num)) return null;
        return Number.isInteger(num) ? `v${num}.0` : `v${num}`;
    }, [minorVersionsByMajor, activeMinorByMajor]);

    const filteredCompareOptions = useMemo(() => {
        const options = (compareOptions || []).filter(option =>
            option.value === 'oldModel' ||
            option.type === 'results_file' ||
            (option.type === 'optune' && option.value !== selectedVersion)
        );
        return options;
    }, [compareOptions, selectedVersion]);

    const handleCompareOpenChange = (nextOpen) => {
        if (nextOpen && filteredCompareOptions.length === 0) {
            sparkToast.error('Nothing to compare');
            setCompareMenuOpen(false);
            return;
        }
        setCompareMenuOpen(nextOpen);
    };

    const compareRows = useMemo(() => {
        const decorate = (option) => ({
            ...option,
            disabled: option.type === 'teacher' && page.compareTeacherDisabled,
            description: option.description
                || (option.type === 'optune' && option.value === latestMajorVersion ? 'Latest' : undefined),
        });
        const groups = [
            ['Teacher', (option) => option.type === 'teacher'],
            ['Results file', (option) => option.type === 'results_file'],
            ['Versions', (option) => option.type === 'optune'],
        ];
        const rows = [];
        groups.forEach(([label, match]) => {
            const matched = filteredCompareOptions.filter(match).map(decorate);
            if (!matched.length) return;
            rows.push({ type: 'section', label });
            rows.push(...matched);
        });
        return rows;
    }, [filteredCompareOptions, page.compareTeacherDisabled, latestMajorVersion]);

    const compareItems = useMemo(() =>
        compareRows.map((option) => (
            option.type === 'section'
                ? { type: 'section', label: option.label }
                : {
                    label: String(option.label),
                    description: option.description,
                    state: option.disabled
                        ? 'disabled'
                        : option.value === currentToCompare ? 'selected' : 'default',
                }
        )), [compareRows, currentToCompare]);

    const compareDisplayText = useMemo(() => {
        if (currentToCompare == null) return undefined;
        if (currentToCompare === 'oldModel' || currentToCompare === 'file' || typeof currentToCompare === 'number') {
            const found = filteredCompareOptions.find(o => o.value === currentToCompare);
            return found ? String(found.label) : String(currentToCompare);
        }
        return String(currentToCompare);
    }, [currentToCompare, filteredCompareOptions]);

    const versionItems = useMemo(() =>
        majorVersionOptions.map(v => {
            const minorLabel = getActiveMinorLabel(v.value);
            return {
                label: minorLabel ? `${v.label} (${minorLabel})` : v.label,
                state: v.value === (selectedMajorVersion ?? selectedVersion) ? 'selected' : 'default',
                suffix: minorLabel && typeof onReplaceBenchmark === 'function' ? (
                    <Button
                        variant="ghost"
                        size="tiny"
                        tooltip="Replace Benchmark"
                        aria-label={`Replace benchmark for ${v.label}`}
                        iconSlug="switch-horizontal"
                        startIcon={<SparkIcon slug="switch-horizontal" size="small" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setVersionMenuOpen(false);
                            onReplaceBenchmark(v.value);
                        }}
                    />
                ) : undefined,
            };
        }), [majorVersionOptions, selectedMajorVersion, selectedVersion, getActiveMinorLabel, onReplaceBenchmark]);

    const versionDisplayText = useMemo(() => {
        const val = selectedMajorVersion ?? selectedVersion;
        if (val == null) return undefined;
        const found = majorVersionOptions.find(v => v.value === val);
        const baseLabel = found ? found.label : `V${val}`;
        const minorLabel = getActiveMinorLabel(val);
        return minorLabel ? `${baseLabel} (${minorLabel})` : baseLabel;
    }, [selectedMajorVersion, selectedVersion, majorVersionOptions, getActiveMinorLabel]);

    const sortedLanguageOptions = useMemo(() => {
        if (!Array.isArray(languageOptions) || languageOptions.length === 0) return [];
        return [
            { value: 'all', label: 'All languages' },
            ...[...languageOptions]
                .sort((a, b) =>
                    getLanguageNameByCode(String(a)).localeCompare(getLanguageNameByCode(String(b)))
                )
                .map(lang => ({ value: lang, label: getLanguageNameByCode(String(lang)) })),
        ];
    }, [languageOptions]);

    const languageItems = useMemo(() =>
        sortedLanguageOptions.map(opt => ({
            label: opt.label,
            state: opt.value === (selectedLanguage ?? 'all') ? 'selected' : 'default',
        })), [sortedLanguageOptions, selectedLanguage]);

    const languageDisplayText = useMemo(() => {
        if (selectedLanguage == null) return 'All languages';
        const found = sortedLanguageOptions.find(o => o.value === selectedLanguage);
        return found ? found.label : String(selectedLanguage);
    }, [selectedLanguage, sortedLanguageOptions]);

    const classOptionsData = useMemo(() => {
        if (hideLabelSelect || typeof getClassOptions !== 'function') return [];
        return getClassOptions();
    }, [hideLabelSelect, getClassOptions]);

    const labelItems = useMemo(() =>
        classOptionsData.map(option => ({
            label: `${option.label}${option.count != null ? ` (${option.count})` : ''}`,
            state: option.value === selectedLabel ? 'selected' : 'default',
        })), [classOptionsData, selectedLabel]);

    const labelDisplayText = useMemo(() => {
        if (selectedLabel == null) return undefined;
        const found = classOptionsData.find(o => o.value === selectedLabel);
        return found ? String(found.label) : String(selectedLabel);
    }, [selectedLabel, classOptionsData]);

    const matchTypeItems = useMemo(() => [
        { label: 'Exact match', state: selectedMatchType === 'exact' ? 'selected' : 'default' },
        { label: 'Partial entity detection', state: selectedMatchType === 'partial' ? 'selected' : 'default' },
    ], [selectedMatchType]);

    const matchTypeDisplayText = useMemo(() => {
        if (selectedMatchType === 'exact') return 'Exact match';
        if (selectedMatchType === 'partial') return 'Partial entity detection';
        return undefined;
    }, [selectedMatchType]);

    const openParamsModal = () => {
        if (!localUsecase || !selectedVersion || !isLatestMajor || isRefreshing) return;
        setParamsModalOpen(true);
    };

    const handleRefresh = async ({ evaluationScope = 'full', benchmarkNames } = {}) => {
        if (!localUsecase || !selectedVersion || !isLatestMajor) return;
        setParamsModalOpen(false);
        lastRunRef.current = { evaluationScope, benchmarkNames };

        try {
            setIsRefreshing(true);
            onRefreshStart?.();

            const body = {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                version: selectedVersion,
                evaluation_scope: evaluationScope,
            };
            if (Array.isArray(benchmarkNames)) {
                body.benchmark_names = benchmarkNames;
            }

            const response = await data_from_server_post('recalculate-evaluation', body);

            if (response?.success) {
                const newVersion = response?.new_version ?? null;
                sparkToast.success("Re evaluation triggered successfully");
                if (typeof newVersion === "number" && Number.isFinite(newVersion)) {
                    setSelectedVersion(Math.floor(newVersion));
                } else if (typeof selectedMajorVersion === "number" && Number.isFinite(selectedMajorVersion)) {
                    setSelectedVersion(selectedMajorVersion);
                }
                onRefreshComplete?.({ success: true, response });
            } else {
                sparkToast.error(response?.message || 'Failed to trigger evaluation recalculation');
                onRefreshComplete?.({ success: false, response });
            }
        } catch (error) {
            console.error('Error triggering evaluation recalculation:', error);
            sparkToast.error('Failed to trigger evaluation recalculation');
            onRefreshComplete?.({ success: false, error });
        } finally {
            setIsRefreshing(false);
        }
    };

    const onBannerAction = () => {
        if (page.action === 'Retrain') {
            onRetrain?.();
            return;
        }
        if (page.action === 'Retry') {
            handleRefresh(lastRunRef.current);
            return;
        }
        openParamsModal();
    };

    const shouldShowImproveButton = showImproveEvals && !isSummarization && !isNerTaskType;
    const showAdjustThresholdButton = typeof onPlayWithThreshold === 'function';
    // Task-management tour, "Tools to improve accuracy" mark: it points at the
    // Adjust Threshold + Improve pair, so it only applies while at least one
    // of them is on screen (the threshold view hides the whole row).
    const hasEvaluationTools = !isThresholdMode && (showAdjustThresholdButton || shouldShowImproveButton);
    useWalkthroughContextValue({ evaluationTools: { hasTools: hasEvaluationTools } });

    const getLabelPlaceholder = () => {
        if (taskType === TaskType.NER_WITH_RELATIONS) {
            return activeTab === 'entities' ? 'All entities' : 'All relations';
        }
        return taskType === TaskType.NER ? 'All entities' : 'All labels';
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            width: '100%',
            marginTop: 0,
            marginBottom: 32,
            borderRadius: 12,
            gap: 12,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: 16,
            }}>
                {isThresholdMode && (
                    <button
                        type="button"
                        className="fs-unmask"
                        onClick={onBackFromThreshold}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            background: 'none',
                            border: 'none',
                            padding: 4,
                            cursor: 'pointer',
                            flexShrink: 0,
                            color: 'var(--semantic-content-primary, #1a1b23)',
                            fontFamily: "var(--font-family-platform, 'Figtree', sans-serif)",
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: '22px',
                        }}
                    >
                        <SparkIcon slug="arrow-left" size="small" />
                        <span>Back</span>
                    </button>
                )}
                <EvaluationRunStatus
                    runDateIso={runDateIso}
                    isLatest={isLatestEvaluation}
                    version={runStatusVersion}
                    onSwitchToLatest={onSwitchToLatest}
                />
                {!isThresholdMode && (
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0, gap: 10 }}>
                    <Button
                        className="fs-unmask"
                        variant="secondary"
                        size="medium"
                        showStartIcon
                        startIcon={<SparkIcon slug="report-refresh" size="medium" />}
                        onClick={openParamsModal}
                        disabled={!isLatestMajor || isRefreshing || page.evaluationButtonDisabled}
                        disableWhenReadOnly
                    >
                        Re-evaluate
                    </Button>
                    {hasEvaluationTools && (
                        // Anchor of the task-management tour's "Tools to
                        // improve accuracy" mark (Figma 9608:21761 highlights
                        // Adjust Threshold + Improve together, not Revaluate).
                        <div
                            data-walkthrough="evaluation-tools"
                            style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                        >
                            {showAdjustThresholdButton && (
                                <Button
                                    className="fs-unmask"
                                    variant="secondary"
                                    size="medium"
                                    showStartIcon
                                    startIcon={<SparkIcon slug="adjust" size="medium" />}
                                    onClick={onPlayWithThreshold}
                                >
                                    Adjust Threshold
                                </Button>
                            )}
                            {shouldShowImproveButton && (
                                <Button
                                    className="fs-unmask"
                                    variant="secondary"
                                    size="medium"
                                    showStartIcon
                                    startIcon={<SparkIcon slug="trending-up" size="medium" />}
                                    onClick={() => (onImproveClick ? onImproveClick() : moveToScreen?.(localUsecase?._id, "data-selection", true))}
                                    disableWhenReadOnly
                                >
                                    {improveButtonLabel}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                )}
            </div>
            {page.text ? (
                <AlertBanner
                    feedback={page.severity || 'info'}
                    text={page.text}
                    showButton={Boolean(page.action)}
                    buttonLabel={page.action || 'Re-evaluate'}
                    buttonType="text"
                    showCloseButton={page.dismissible}
                    onActionClick={onBannerAction}
                    onCloseClick={() => setSuccessDismissed(true)}
                />
            ) : null}
            {page.tags.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {page.tags.map((tag) => (
                        tag.opensDiff ? (
                            <button
                                key={tag.label}
                                type="button"
                                onClick={() => {
                                    setDiffSide(tag.label === 'Specific prompt changed' ? 'specific' : 'teacher');
                                    setParamsModalOpen(true);
                                }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            >
                                <TagFeedback label={tag.label} variant={tag.variant} />
                            </button>
                        ) : (
                            <TagFeedback key={tag.label} label={tag.label} variant={tag.variant} />
                        )
                    ))}
                </div>
            ) : null}
            <div style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'nowrap',
                alignItems: 'flex-end',
            }}>
                <div className={currentToCompare === 'oldModel' ? 'fs-unmask compare-previous-teacher' : 'fs-unmask'}>
                    <DropdownMenu
                        size="medium"
                        label="Compare"
                        className={currentToCompare === 'oldModel' ? 'compare-previous-teacher' : ''}
                        placeholder="Compare with"
                        text={compareDisplayText}
                        showLabel
                        items={compareItems}
                        value={compareDisplayText}
                        showClearButton
                        open={compareMenuOpen}
                        onOpenChange={handleCompareOpenChange}
                        onClear={() => setCurrentToCompare(null)}
                        onSelect={(item, index) => {
                            const opt = compareRows[index];
                            if (!opt || opt.type === 'section' || opt.disabled) return;
                            setCurrentToCompare(opt.value ?? null);
                        }}
                    />
                </div>
                <div>
                    <DropdownMenu
                        size="medium"
                        label="To"
                        placeholder="Version"
                        text={versionDisplayText}
                        showLabel
                        items={versionItems}
                        panelTitle="Versions"
                        value={versionDisplayText}
                        open={versionMenuOpen}
                        onOpenChange={setVersionMenuOpen}
                        onSelect={(item, index) => {
                            const opt = majorVersionOptions[index];
                            if (opt) setSelectedVersion(Math.floor(Number(opt.value)));
                        }}
                    />
                </div>
                {!hideLanguageSelect &&
                    Array.isArray(languageOptions) &&
                    languageOptions.length > 0 &&
                    typeof setSelectedLanguage === 'function' && (
                        <DropdownMenu
                            size="medium"
                            label="Language"
                            placeholder="All languages"
                            text={languageDisplayText}
                            showLabel
                            items={languageItems}
                            value={languageDisplayText}
                            onSelect={(item, index) => {
                                const opt = sortedLanguageOptions[index];
                                if (opt) setSelectedLanguage(opt.value === 'all' ? null : opt.value);
                            }}
                        />
                    )}
                {!hideLabelSelect && (
                    <DropdownMenu
                        size="medium"
                        label={taskType === TaskType.NER_WITH_RELATIONS
                            ? (activeTab === 'entities' ? 'Entity types' : 'Relations')
                            : (taskType === TaskType.NER ? 'Entity types' : 'Labels')}
                        placeholder={getLabelPlaceholder()}
                        text={labelDisplayText}
                        showLabel
                        items={labelItems}
                        value={labelDisplayText}
                        onSelect={(item, index) => {
                            const opt = classOptionsData[index];
                            if (opt) setSelectedLabel(opt.value);
                        }}
                    />
                )}
                {!hideLabelSelect && (taskType === TaskType.NER || (taskType === TaskType.NER_WITH_RELATIONS && activeTab === "entities")) && (
                    <DropdownMenu
                        size="medium"
                        label="Entity detection"
                        placeholder="Select entity match type"
                        text={matchTypeDisplayText}
                        showLabel
                        items={matchTypeItems}
                        value={matchTypeDisplayText}
                        onSelect={(item) => {
                            const val = item.label === 'Exact match' ? 'exact' : 'partial';
                            setSelectedMatchType(val);
                        }}
                    />
                )}
            </div>
            <RecalculateEvaluationParamsModal
                open={paramsModalOpen}
                usecase={localUsecase}
                changeStatus={changeStatus}
                confirming={isRefreshing}
                initialDiffSide={diffSide}
                runDateIso={runDateIso}
                onCancel={() => {
                    setDiffSide(null);
                    setParamsModalOpen(false);
                }}
                onConfirm={handleRefresh}
                uploadBenchmark={async ({ file, mapping, name }) => {
                    const { uploadDatasetFile } = await import('@/features/datasets/utils/datasetUploadFlow');
                    let uploadedName = name;
                    await uploadDatasetFile({
                        file,
                        type: 'benchmark',
                        currentUsecase: localUsecase,
                        selectedTaskType: taskType,
                        exampleColumnName: mapping?.example || 'example',
                        labelColumnName: mapping?.label || mapping?.labels || 'labels',
                        context: {
                            setProgressStatus: () => {},
                            updateUsecase: () => {},
                            registerUploadAbort: () => {},
                            unregisterUploadAbort: () => {},
                            registerUploadGroup: () => {},
                            clearCancelledUploadSuppression: () => {},
                        },
                        callbacks: {
                            onBenchmarkSuccess: (uploaded) => { uploadedName = uploaded || name; },
                            onUploadSuccess: () => {},
                            refreshLibraryOptions: () => {},
                            setIsDataAnalysisLoading: () => {},
                        },
                    });
                    return uploadedName;
                }}
            />
        </div>
    );
};

export default FilterBar;
